---
title: A task page
summary: "A task page is where you work one step of a record's process: read what it needs, fill its form, and press the exit that says how it ended."
visibility: authenticated
feature: process_engine
audience: end-user
section: Pages
order: 50
last_verified: 2026-10-21
owner: docs
routes: ['/tasks/:taskId']
aliases: [task, step, complete a step, finish a step, work a step, form, claim]
tour:
  - anchor: task.header.title
    text: "The step's number and title, and the record it belongs to. The breadcrumb takes you back where you came from."
  - anchor: task.header.claim
    text: "Claim takes the task so others on your seat see it is yours."
    when_missing: "The task is already yours, or it is done."
  - anchor: task.form.needs
    text: "Needs shows what this step reads from earlier steps, as it stands now."
  - anchor: task.form.fields
    text: "The fields are what this step produces. A pre-filled field says where its value came from."
  - anchor: task.form.actions
    text: "Each button is a way the step can end, such as done, decline or need more. A button stays off until the fields it needs are filled."
  - anchor: task.form.save
    text: "Save draft keeps what you typed without finishing the step."
  - anchor: task.done.reopen
    text: "On a done task, Re-open sends the process back to this step, with a reason."
    when_missing: "The task is still open, or you may not re-open it."
---

# A task page

A record's process (for example the charter quote) runs one step at a time. A step
worked by a person opens a task, and the task page is where you work it. Open a task
from **Tasks**, from **My work** on Home, or from the record's Tasks section.

## Complete a step

1. Read the header: the step's number and title, the record, the seat it belongs to and
   when it is due.
2. Press **Claim** to take it, so others on your seat see it is yours.
3. Read **Needs**: what the step uses from earlier steps (the intake, a chosen study,
   the cost), as it stands now.
4. Fill the fields. A field the AI or an earlier step pre-filled says so; a value you
   change shows the pre-filled one struck through, with **Use the pre-filled value** to
   put it back.
5. Press the button for how the step ended. Each button names an exit (**done**,
   **decline**, **need more** and so on); one stays off, with a hint, until the fields it
   needs are filled.

**Save draft** keeps your typing without finishing the step. Finishing a step you opened
from its record opens that record with its process band in view and the new current step
marked; from My work or Home it returns you there. Either way a message says what
happened, and the record moves to its next step.

## A step the AI proposed

Some steps are first worked by the AI: the form opens pre-filled, marked
**AI suggestion** with its confidence, and the exit it proposed is ringed.
**Why the AI suggested this** shows its reasoning. Accept it by pressing that exit, or
change any field first.

## Steps that wait

A step that waits for something on the record (a feasibility result, a sent quote, the
customer's answer) opens no form. Its page says what ends it and offers a button to the
place on the record where you record it, such as **Record feasibility result**.

## Restricted fields

A step whose form holds pricing figures needs the pricing permission. Without it the
page reads **Restricted** and says that working the task needs the pricing permission;
you can still see the task, and an admin can grant the permission through your role.

## Re-open a completed step

On a done task, or from the record's **Completed tasks**, press **Re-open**, write why,
and press **Re-open step**. The process goes back to that step with a new task,
pre-filled with the current values; later steps' values are kept and flagged as stale.
A closed record cannot be re-opened this way.

<FeatureGate feature="compass">

## The charter quote's steps

The charter quote runs fourteen steps. The ones worked here:

- **2 Qualify the counterparty** (Commercial): proceed, decline, or ask for more.
- **3 Qualify the cargo** (Ground Operations): the AI proposes whether the cargo is
  loadable; confirm with done, or mark it not loadable.
- **4 Aircraft and rotation** (Commercial): choose the **Aircraft type** and lay out the
  sectors. Each sector is **Live sector** or positioning (**Flown as**) with its
  **Payload**; the weight is pre-filled from the intake.
- **5 Dispatch request** (Dispatch): pick a route study, or build without one, and choose the
  AIRAC cycle. For a study, **Your choice** asks **Use it as it is** (the record goes to step 7)
  or **Start a new version from it** (step 6); building **Without a study** goes to step 6. The
  button then reads what it will do, for example "Use RS-142 v3" or **Assemble a new study**.
- **6 Route study computed** (Dispatch): build or adjust the study (below).
- **8 Build the cost** (Commercial): the cost sheet, pre-filled from the study and the
  reference data under the **Pricing basis**; change any line, and say why under
  **Changes from the study** when you change a fuel price. Press **Confirm the cost sheet**,
  or **Ask Dispatch for a new study**.
- **9 Rate references**: the cost-based minimum and the references for the rate.
- **10 Propose the rate** (Pricing): the target rate per block hour and the price
  offered, in the **ACMI per block hour** card at the top of the page. Type them in
  the card's boxes, or drag them on its scale: the diamond, a pin standing above the
  track, is the target, and the dot on the track is the price, drawn at the rate it
  keeps. To use the keyboard, focus
  **Price offered on the scale** or **Target rate per block hour on the scale** and
  press the arrow keys (Shift for bigger steps). The line under the scale reads
  **Price offered** − **Direct operating cost** = **Operating margin**, then
  **Achieved against target**, and a bracket warns when the target is short of the
  cost minimum. Add why, and the customer's price as one figure or a range, then press
  **Submit for approval**.
- **11 Price and decide** (Management): read the same card, read-only, then
  **Approve**, **Reject** with what to change, or decline on economics.
- **14 Reconcile and report** (Finance): the actual cost and the variance; **update**
  saves a part and keeps the step open, done finishes it.

Step 7 (the feasibility result), step 12 (the offer) and step 13 (the customer's
answer) wait, and are worked on the record page.

</FeatureGate>

<FeatureGate feature="compass_studies">

## Choose a route study (step 5)

Under **Route study for this rotation**, the list has four parts. **Covers this rotation**
holds the studies that fly a leg of the rotation directly, marked `covers every leg` or
`covers the live leg`. **Without a study** comes next, **Other studies** (the ones that
share a station) is folded away until you open it or type a search, and **Another study by
reference** comes last, for a study that is not in the list: type its reference there. Type a
station into **Search studies** to narrow the list, and press a study to choose it. Nothing is
chosen until you do. **Your choice** then shows the two things you can do with it: **Use it as
it is** (the quote is costed on that study and the record goes to step 7) or **Start a new
version from it** (step 6 opens pre-filled from it). Choose the cycle under **Checked against
AIRAC**, and press the button, which then reads what it will do, for example "Use RS-142 v3"
or "Start a new version from RS-142 v3". The page shows what it writes to the record, and the
line beside the button says what comes next. When you come back to the step and your choice
differs from the last round's, an optional field asks why ("Why this study instead of" the earlier
pin, or "Why a new study instead of" it when you build without a study).

When no study covers the routing, choose **Start from the sectors alone** or, where the
reference data is set up, **Assemble from reference data**, both under **Without a study**,
and press **Build a new study from the sectors** or **Assemble a new study**: the legs are then
built from the reference data at step 6.

## When no study leg matches: build one (step 6)

You can create a route study in the application; you do not need a new workbook.
Step 6 opens the study editor: a **Legs** table with a row per sector, holding the base
study's figures in the legs it matched and a row marked `new` for a sector it did not,
and a **Charges at each stop** table. Type the figures,
**Add a leg** for another leg, **Add a charge** for another charge line, and press
**Save as study**. The study is saved as a new route study (or a new version of the
base), and its reference is written into the step. Then press **done**.

A routing assembled from the reference data can finish with **done** without saving a
study. **Use a reference instead** lets you type an existing study's reference.

</FeatureGate>
